import { LogOut, Plus, X } from 'lucide-react';
import React from 'react';
import { useNavigate } from 'react-router';
import { Badge } from '@components/ui/badge';
import { Button } from '@components/ui/button';
import { injectTSDI } from '../core/tsdi';
import { Api } from '../core/api';
import {
    CellProps,
    ReactColumn,
    SortingRule,
    TableComponent
} from '../components/table';
import {
    TenantPageEntrySupportDto,
    TenantStatus
} from '../core/api/TenantClient';
import { links } from '../core/router';
import { Filters } from '../components/filters';
import { PageHeader } from '../components/page-header';
import { I18n } from '../core/i18n';
import { useStore } from './store';
import { TenantDeactivateModal } from './modals/tenant-deactivate-modal';
import { TenantActivateModal } from './modals/tenant-activate-modal';

export const TenantList = () => {
    const { corporateClient, googleUserAuth } = injectTSDI(Api);
    const { formatShortDate } = injectTSDI(I18n);
    const navigate = useNavigate();

    const {
        tenants,
        searchParams,
        statusFilter,
        typeFilter,
        sorting,
        productType,
        isCorporateProductType,
        isTeamsProductType,
        setSorting,
        setStatusFilter,
        setTypeFilter,
        setSearchTerm,
        dispatch
    } = useStore();

    const openDeactivateState = React.useState(false);
    const [tenantToDeactivate, setTenantToDeactivate] = React.useState<
        TenantPageEntrySupportDto | undefined
    >(undefined);
    const openActivateState = React.useState(false);
    const [tenantToActivate, setTenantToActivate] = React.useState<
        TenantPageEntrySupportDto | undefined
    >(undefined);

    const handleLogin = async (tenant: string) => {
        const session =
            await corporateClient.support.corporateSupportControllerCreateSession(
                {
                    email: googleUserAuth?.email,
                    firstName: googleUserAuth?.firstName,
                    lastName: googleUserAuth?.lastName
                },
                {
                    headers: {
                        'x-tenant-id': tenant
                    }
                }
            );
        if (session.data.redirectUrl) {
            window.open(session.data.redirectUrl, '_blank');
        }
    };

    const handleActivate = async (tenant: TenantPageEntrySupportDto) => {
        setTenantToActivate(tenant);
        const [, setOpen] = openActivateState;
        setOpen(true);
    };

    const handleDeactivate = async (tenant: TenantPageEntrySupportDto) => {
        setTenantToDeactivate(tenant);
        const [, setOpen] = openDeactivateState;
        setOpen(true);
    };
    const onSortingChange = (sorting?: SortingRule) => {
        if (sorting) {
            if (sorting.id === 'status') {
                setSorting((previous) => {
                    if (!previous) {
                        return previous;
                    }
                    return Array.isArray(previous)
                        ? [sorting, ...previous]
                        : [sorting, previous];
                });
                return;
            }
            setSorting(sorting);
        }
    };

    const createTenantHandler = () => {
        navigate(links.tenantOnboarding());
    };

    const clearFilters = () => {
        setStatusFilter([]);
        setTypeFilter([]);
    };

    const refreshTenants = () =>
        void tenants.request([
            searchParams,
            statusFilter,
            typeFilter,
            productType
        ]);

    return (
        <>
            <PageHeader
                title="Tenants"
                actions={
                    productType === 'TEAMS' && (
                        <Button
                            onClick={createTenantHandler}
                            disabled={!productType}
                        >
                            <Plus />
                            Create tenant
                        </Button>
                    )
                }
            />
            <TenantDeactivateModal
                openState={openDeactivateState}
                tenant={tenantToDeactivate}
                onSuccess={refreshTenants}
            />
            <TenantActivateModal
                openState={openActivateState}
                tenant={tenantToActivate}
                onSuccess={refreshTenants}
            />
            <div className="mb-4 flex flex-wrap items-end gap-3">
                <Filters<TenantPageEntrySupportDto['status'], true>
                    multiple
                    label="Status"
                    width={200}
                    value={statusFilter}
                    options={[
                        { label: 'active', value: 'ACTIVE' },
                        { label: 'inactive', value: 'INACTIVE' },
                        { label: 'locked', value: 'LOCKED' }
                    ]}
                    onChange={(val) => void setStatusFilter(val)}
                />

                <Filters<TenantPageEntrySupportDto['type'], true>
                    multiple
                    label="Type"
                    width={200}
                    value={typeFilter}
                    options={[
                        { label: 'client', value: 'CLIENT' },
                        { label: 'former client', value: 'FORMER_CLIENT' },
                        { label: 'BASE', value: 'MYSPORTS_BASE' },
                        { label: 'partner', value: 'PARTNER' },
                        { label: 'demo', value: 'DEMO' },
                        { label: 'open demo', value: 'OPEN_DEMO' },
                        { label: 'unmanaged', value: 'UNMANAGED' }
                    ]}
                    onChange={(val) => void setTypeFilter(val)}
                />
                <Button variant="ghost" onClick={() => void clearFilters()}>
                    Clear
                    <X />
                </Button>
            </div>
            <TableComponent<TenantPageEntrySupportDto[]>
                items={tenants}
                key={productType || 'NONE'}
                tableLayout="auto"
                columns={[
                    {
                        Header: 'Tenant ID',
                        accessor: 'name',
                        style: { width: 200 }
                    },
                    {
                        Header: 'Status',
                        accessor: 'status',
                        style: { width: 120 },
                        Cell: ({
                            row: {
                                original: { status }
                            }
                        }: CellProps<TenantPageEntrySupportDto>) => (
                            <TenantStatusBadge status={status} />
                        )
                    },
                    {
                        Header: 'Type',
                        accessor: 'type'
                    },
                    {
                        Header: 'Source',
                        accessor: 'source',
                        Cell: ({
                            row: {
                                original: { source }
                            }
                        }: CellProps<TenantPageEntrySupportDto>) => (
                            <span className="block truncate">
                                {getTenantSourceName(source)}
                            </span>
                        )
                    },
                    ...(isTeamsProductType
                        ? [
                              {
                                  Header: 'Licenses',
                                  accessor: 'allowedLicenses',
                                  disableSortBy: true,
                                  Cell: ({
                                      row: {
                                          original: { allowedLicenses }
                                      }
                                  }: CellProps<TenantPageEntrySupportDto>) => (
                                      <div className="text-center">
                                          {allowedLicenses}
                                      </div>
                                  )
                              } as ReactColumn<TenantPageEntrySupportDto>
                          ]
                        : []),
                    ...(isCorporateProductType
                        ? [
                              {
                                  Header: 'Contract',
                                  accessor: 'contractModel',
                                  disableSortBy: true,
                                  style: { width: 120 }
                              } as ReactColumn<TenantPageEntrySupportDto>
                          ]
                        : []),
                    {
                        Header: 'Employees',
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        accessor: 'company.connectedEmployeeCount' as any,
                        Cell: ({
                            row: {
                                original: { connectedEmployeeCount }
                            }
                        }: CellProps<TenantPageEntrySupportDto>) => (
                            <div className="text-center">
                                {connectedEmployeeCount}
                            </div>
                        )
                    },
                    {
                        Header: 'Created On',
                        accessor: 'createdDate',
                        Cell: ({
                            row: {
                                original: { createdDate }
                            }
                        }: CellProps<TenantPageEntrySupportDto>) =>
                            formatShortDate(createdDate)
                    },
                    {
                        Header: '',
                        id: 'button',
                        style: { width: 100 },
                        Cell: ({
                            row: {
                                original: { name, status }
                            }
                        }: CellProps<TenantPageEntrySupportDto>) => (
                            <div className="flex justify-end">
                                {status !== 'LOCKED' && (
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => void handleLogin(name)}
                                    >
                                        Login
                                        <LogOut />
                                    </Button>
                                )}
                            </div>
                        )
                    }
                ]}
                contextMenu={[
                    {
                        label: 'Details',
                        hidden: (value) => value.status === 'LOCKED',
                        action: (value) => {
                            navigate(links.tenantDetails(value.name));
                        }
                    },
                    {
                        label: 'Activate',
                        hidden: (value) => value.status !== 'INACTIVE',
                        action: (value) => {
                            handleActivate(value);
                        }
                    },
                    {
                        label: 'Deactivate',
                        hidden: (value) => value.status !== 'ACTIVE',
                        action: (value) => {
                            handleDeactivate(value);
                        }
                    }
                ]}
                onPageSizeChange={(size) => dispatch({ type: 'size', size })}
                onChangePage={(page) => void dispatch({ type: 'page', page })}
                onSearchChange={(val) => void setSearchTerm(val)}
                onSortingChange={onSortingChange}
                sorting={Array.isArray(sorting) ? sorting[0] : sorting}
            />
        </>
    );
};

interface TenantStatusBadgeProps {
    status: TenantPageEntrySupportDto['status'];
}

export const getTenantSourceName = (
    source?: TenantPageEntrySupportDto['source']
) => {
    switch (source) {
        case 'ADMIN_WEBCLIENT':
            return 'admin tool';
        case 'TRY_CORPORATE_WEBCLIENT':
            return 'try corporate';
        default:
            return source;
    }
};

const statusVariants = {
    [TenantStatus.ACTIVE]: 'success',
    [TenantStatus.INACTIVE]: 'warning',
    [TenantStatus.LOCKED]: 'destructive'
} as const;

export const TenantStatusBadge = ({ status }: TenantStatusBadgeProps) => (
    <Badge variant={statusVariants[status]}>{status}</Badge>
);

export default TenantList;
